import React, { Fragment, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Tooltip } from '@material-ui/core'
import clsx from 'clsx'
import { useAppContext } from '../../../redux/slices/appContext'
import { useSearchClients } from '../../../api/clients'
import { useFormattingContext } from '../FormattingProvider/FormattingContext'
import FlexRow from '../../molecules/FlexRow'
import Icon from '../../atoms/Icon'
import Loading from '../AddAccount/Loading'
import { useHideIfNoDataWhen } from '../HideIfNoData'
import EditIconButton from '../../pages/admin/shared/EditIconButton'
import { KDP } from '../../../policies/admin'
import { KDPDialog } from './KDPDialog'
const iconSizes = {
  sm: '36px',
  md: '50px',
  lg: '75px'
}

const sizes = (theme) => ({
  sm: {
    '& .__metric': {
      fontSize: theme.typography.fontSizes.h1
    }
  },
  md: {
    '& .__metric': {
      fontSize: '50px'
    }
  },
  lg: {
    '& .__metric': {
      fontSize: '75px'
    }
  }
})

const useStyles = makeStyles((theme) => ({
  kdpMetric: ({ size }) => ({
    ...(sizes(theme)[size] || {}),
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    '& .__header': {
      fontWeight: theme.typography.weights.light
    }
  })
}))

const useMetric = (keyDatapoint) => {
  const { clientId } = useAppContext()
  const query = useMemo(() => {
    return {
      limit: 1,
      filters: {
        clientId: [{ op: 'eq', value: clientId }]
      },
      includes: {
        keyDatapoints: [keyDatapoint]
      }
    }
  }, [keyDatapoint, clientId])

  return useSearchClients(query)
}

function KDPMetric ({
  keyDatapoint,
  format,
  className,
  hideName,
  style,
  size,
  tooltip,
  icon,
  headerClassName,
  headerStyles,
  isEditable
}) {
  const classes = useStyles({ size })
  const { data, isLoading } = useMetric(keyDatapoint)
  useHideIfNoDataWhen(() => !isLoading && data?.data[0]?.keyDatapoints[keyDatapoint]?.value === null, [data, isLoading])
  const datapoint = useMemo(() => {
    if (!data?.data?.length) return null
    return data.data.at(0).keyDatapoints[keyDatapoint]
  }, [data, keyDatapoint])
  const canEdit = isEditable
  // eslint-disable-next-line no-unused-vars
  const { formatter } = useFormattingContext()
  const kdpDialogRef = useRef(null)

  if (isLoading) {
    return (
      <div className={clsx(className, classes.kdpMetric)}>
        <Loading size={iconSizes[size]} />
      </div>
    )
  }

  const OuterComponent = tooltip ? Tooltip : Fragment
  return (
    <>
      <KDPDialog ref={kdpDialogRef} />
      <OuterComponent>
        <div className={clsx(className, classes.kdpMetric)} style={style}>
          {!hideName ? (
            <div className={clsx('__header', headerClassName)} style={headerStyles}>{datapoint?.displayName}</div>
          ) : null}
          <FlexRow className='__container'>
            {icon ? (
              <Icon name={icon} customSize={iconSizes[size]} />
            ) : null}
            <div className='__metric'>{formatter(datapoint?.value, format)}</div>
            {canEdit ? (
              <EditIconButton policy={KDP.editKDP} onClick={() => kdpDialogRef.current.open(datapoint, format)} />
            ) : null}
          </FlexRow>
        </div>
      </OuterComponent>
    </>
  )
}

KDPMetric.propTypes = {
  keyDatapoint: PropTypes.string,
  icon: PropTypes.string,
  format: PropTypes.string,
  className: PropTypes.string,
  hideName: PropTypes.bool,
  style: PropTypes.any,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  tooltip: PropTypes.string,
  headerClassName: PropTypes.string,
  headerStyles: PropTypes.object,
  isEditable: PropTypes.bool
}

KDPMetric.defaultProps = {
  size: 'md'
}

export default KDPMetric
